import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { cn } from '../../lib/utils';
import {
  Upload, Link2, FileText, CheckCircle2, ArrowRight, ArrowLeft, Shield,
  Sparkles, Loader2, X, Globe, Server, Key, Database
} from 'lucide-react';
import { toast } from 'sonner';
import AdoptionJourney from '../../components/shared/AdoptionJourney';

const EHR_VENDORS = [
  { id: 'athena', name: 'athenahealth', desc: 'Full FHIR R4 support', popular: true },
  { id: 'practice_fusion', name: 'Practice Fusion', desc: 'FHIR STU3 / R4', popular: true },
  { id: 'kareo', name: 'Kareo / Tebra', desc: 'FHIR R4 API', popular: true },
  { id: 'drchrono', name: 'DrChrono', desc: 'FHIR R4 compliant', popular: false },
  { id: 'allscripts', name: 'Allscripts', desc: 'FHIR R4', popular: false },
  { id: 'eclinicalworks', name: 'eClinicalWorks', desc: 'FHIR R4', popular: false },
  { id: 'nextgen', name: 'NextGen', desc: 'FHIR STU3 / R4', popular: false },
  { id: 'enable', name: 'Enable Healthcare', desc: 'Native integration — full sync', popular: true, native: true },
  { id: 'other', name: 'Other EHR', desc: 'Custom FHIR endpoint', popular: false },
];

const LuminaIntegrations = () => {
  const [path, setPath] = useState(null); // 'ccda' | 'fhir' | null
  const [fhirStep, setFhirStep] = useState(1);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [fhirUrl, setFhirUrl] = useState('');
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [connecting, setConnecting] = useState(false);
  const [connected, setConnected] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [ccdaProcessing, setCcdaProcessing] = useState(false);
  const [ccdaDone, setCcdaDone] = useState(false);

  const handleFhirConnect = () => {
    setConnecting(true);
    setTimeout(() => {
      setConnecting(false);
      setFhirStep(3);
    }, 2500);
  };

  const handleFhirAuthorize = () => {
    setConnecting(true);
    setTimeout(() => {
      setConnecting(false);
      setConnected(true);
      setFhirStep(4);
      toast.success('EHR Connected', { description: `Connected to ${selectedVendor.name} via FHIR` });
    }, 2000);
  };

  const handleCcdaUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) setUploadedFile(file);
  };

  const handleCcdaProcess = () => {
    setCcdaProcessing(true);
    setTimeout(() => {
      setCcdaProcessing(false);
      setCcdaDone(true);
      toast.success('CCDA processed successfully', { description: 'Patient chart populated with clinical data' });
    }, 2500);
  };

  const resetWizard = () => {
    setPath(null);
    setFhirStep(1);
    setSelectedVendor(null);
    setFhirUrl('');
    setClientId('');
    setClientSecret('');
    setConnecting(false);
    setConnected(false);
    setUploadedFile(null);
    setCcdaProcessing(false);
    setCcdaDone(false);
  };

  // Landing: Choose path
  if (!path) {
    return (
      <div className="space-y-8" data-testid="integrations-page">
        <div>
          <h1 className="text-2xl font-bold font-['Plus_Jakarta_Sans'] text-slate-900">Integrations</h1>
          <p className="text-slate-500 mt-1">Connect your EHR or upload patient data to get started</p>
        </div>

        {/* Adoption Journey */}
        <AdoptionJourney app="lumina" />

        {/* Native Integration CTA */}
        <Card className="border-violet-200 bg-gradient-to-r from-violet-50/50 to-white" data-testid="enable-native-cta">
          <CardContent className="p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="h-14 w-14 rounded-2xl bg-violet-100 flex items-center justify-center">
                <Sparkles className="h-7 w-7 text-violet-600" />
              </div>
              <div>
                <p className="font-bold text-lg text-slate-900">Enable Healthcare Users</p>
                <p className="text-sm text-muted-foreground">Full native integration — appointments, charts, and SOAP notes sync automatically. No setup required.</p>
              </div>
            </div>
            <Button className="bg-black hover:bg-gray-800 gap-2 shrink-0" onClick={() => { setPath('fhir'); setSelectedVendor(EHR_VENDORS.find(v => v.id === 'enable')); }}>
              <Link2 className="h-4 w-4" /> Connect Enable
            </Button>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Path 1: CCDA Upload */}
          <Card className="cursor-pointer hover:border-slate-400 hover:shadow-sm transition-all group" onClick={() => setPath('ccda')} data-testid="ccda-path-card">
            <CardContent className="p-8 text-center space-y-4">
              <div className="h-16 w-16 rounded-2xl bg-violet-50 flex items-center justify-center mx-auto group-hover:bg-violet-100 transition-colors">
                <Upload className="h-8 w-8 text-violet-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold">Upload CCDA</h3>
                <p className="text-sm text-muted-foreground mt-1">Upload patient CCDA/CCD files to auto-populate patient charts with structured clinical data</p>
              </div>
              <Badge variant="outline" className="text-xs">Quick Start</Badge>
            </CardContent>
          </Card>

          {/* Path 2: FHIR Integration */}
          <Card className="cursor-pointer hover:border-slate-400 hover:shadow-sm transition-all group" onClick={() => setPath('fhir')} data-testid="fhir-path-card">
            <CardContent className="p-8 text-center space-y-4">
              <div className="h-16 w-16 rounded-2xl bg-blue-50 flex items-center justify-center mx-auto group-hover:bg-blue-100 transition-colors">
                <Link2 className="h-8 w-8 text-blue-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold">Connect via FHIR</h3>
                <p className="text-sm text-muted-foreground mt-1">Link your EHR for automatic appointment sync, patient charts, and SOAP note push-back</p>
              </div>
              <Badge className="bg-violet-50 text-violet-700 border-violet-200 text-xs">Recommended</Badge>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // CCDA Upload Path
  if (path === 'ccda') {
    return (
      <div className="space-y-6 max-w-2xl mx-auto" data-testid="ccda-wizard">
        <Button variant="ghost" className="gap-2" onClick={resetWizard}><ArrowLeft className="h-4 w-4" /> Back to Integrations</Button>

        <div className="text-center space-y-2">
          <h1 className="text-2xl font-bold font-['Plus_Jakarta_Sans']">Upload CCDA File</h1>
          <p className="text-muted-foreground">Upload a C-CDA or CCD XML file to populate a patient chart</p>
        </div>

        {!ccdaDone ? (
          <Card>
            <CardContent className="p-6 space-y-4">
              {!uploadedFile ? (
                <label className="block">
                  <div className="border-2 border-dashed border-slate-300 rounded-xl p-10 text-center hover:border-violet-400 hover:bg-violet-50/20 cursor-pointer transition-colors">
                    <Upload className="h-10 w-10 text-slate-400 mx-auto mb-3" />
                    <p className="font-medium text-slate-600">Drop CCDA file here or click to browse</p>
                    <p className="text-sm text-muted-foreground mt-1">Supports XML (.xml), CCD (.ccd) files</p>
                  </div>
                  <input type="file" className="hidden" accept=".xml,.ccd,.json" onChange={handleCcdaUpload} data-testid="ccda-file-input" />
                </label>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center justify-between rounded-lg border bg-slate-50 p-4">
                    <div className="flex items-center gap-3">
                      <FileText className="h-6 w-6 text-violet-600" />
                      <div>
                        <p className="text-sm font-medium">{uploadedFile.name}</p>
                        <p className="text-xs text-muted-foreground">{(uploadedFile.size / 1024).toFixed(1)} KB</p>
                      </div>
                    </div>
                    <Button variant="ghost" size="icon" onClick={() => setUploadedFile(null)}><X className="h-4 w-4" /></Button>
                  </div>
                  <Button onClick={handleCcdaProcess} disabled={ccdaProcessing} className="w-full bg-black hover:bg-gray-800 gap-2" data-testid="process-ccda-btn">
                    {ccdaProcessing ? <><Loader2 className="h-4 w-4 animate-spin" /> Processing CCDA...</> : <><Sparkles className="h-4 w-4" /> Process & Create Patient Chart</>}
                  </Button>
                </div>
              )}
              <div className="rounded-lg border border-violet-200 bg-violet-50 p-3 flex items-start gap-2">
                <Sparkles className="h-4 w-4 text-violet-600 mt-0.5" />
                <div className="text-xs text-violet-700 space-y-1">
                  <p className="font-medium">What gets extracted:</p>
                  <p>Patient demographics, problem list, medications, allergies, lab results, procedures, and encounter history.</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card className="text-center" data-testid="ccda-success">
            <CardContent className="p-8 space-y-4">
              <div className="h-16 w-16 rounded-full bg-violet-100 flex items-center justify-center mx-auto">
                <CheckCircle2 className="h-8 w-8 text-violet-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold">Patient Chart Created</h2>
                <p className="text-sm text-muted-foreground mt-1">Successfully extracted data from CCDA file</p>
              </div>
              <div className="rounded-lg border bg-slate-50 p-4 text-left text-sm space-y-1 max-w-sm mx-auto">
                <div className="flex justify-between"><span className="text-muted-foreground">Problems</span><span className="font-medium">4 conditions</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Medications</span><span className="font-medium">6 active</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Allergies</span><span className="font-medium">2 recorded</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Lab Results</span><span className="font-medium">12 results</span></div>
              </div>
              <div className="flex gap-2 justify-center pt-2">
                <Button variant="outline" onClick={resetWizard}>Upload Another</Button>
                <Button className="bg-black hover:bg-gray-800 gap-2"><ArrowRight className="h-4 w-4" /> View Patient Chart</Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    );
  }

  // FHIR Integration Path
  return (
    <div className="space-y-6 max-w-2xl mx-auto" data-testid="fhir-wizard">
      <Button variant="ghost" className="gap-2" onClick={resetWizard}><ArrowLeft className="h-4 w-4" /> Back to Integrations</Button>

      {/* Steps */}
      <div className="flex items-center justify-center gap-2 mb-4">
        {[1, 2, 3, 4].map((s, i) => (
          <div key={s} className="flex items-center gap-2">
            <div className={cn(
              "h-8 w-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors",
              fhirStep >= s ? "bg-black text-white" : "bg-slate-200 text-slate-500"
            )}>
              {fhirStep > s ? <CheckCircle2 className="h-4 w-4" /> : s}
            </div>
            {i < 3 && <div className={cn("w-8 h-px", fhirStep > s ? "bg-black" : "bg-slate-200")} />}
          </div>
        ))}
      </div>

      {/* Step 1: Select Vendor */}
      {fhirStep === 1 && (
        <Card>
          <CardHeader className="text-center">
            <CardTitle className="text-xl">Select Your EHR</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {EHR_VENDORS.map(v => (
              <div
                key={v.id}
                className={cn(
                  "flex items-center justify-between p-4 rounded-xl border cursor-pointer transition-all hover:border-slate-400",
                  selectedVendor?.id === v.id && "border-black bg-slate-50",
                  v.native && "border-violet-200 bg-violet-50/30"
                )}
                onClick={() => setSelectedVendor(v)}
                data-testid={`vendor-${v.id}`}
              >
                <div className="flex items-center gap-3">
                  <div className={cn("h-10 w-10 rounded-lg flex items-center justify-center", v.native ? "bg-violet-100" : "bg-slate-100")}>
                    <Globe className="h-5 w-5 text-slate-500" />
                  </div>
                  <div>
                    <p className="font-medium text-sm">{v.name}</p>
                    <p className="text-xs text-muted-foreground">{v.desc}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {v.native && <Badge className="bg-violet-100 text-violet-700 text-xs">Native</Badge>}
                  {v.popular && !v.native && <Badge variant="outline" className="text-xs">Popular</Badge>}
                  {selectedVendor?.id === v.id && <CheckCircle2 className="h-5 w-5 text-violet-600" />}
                </div>
              </div>
            ))}
            <Button
              className="w-full bg-black hover:bg-gray-800 mt-4"
              disabled={!selectedVendor}
              onClick={() => setFhirStep(2)}
              data-testid="select-vendor-btn"
            >
              Continue with {selectedVendor?.name || '...'}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Step 2: Authentication */}
      {fhirStep === 2 && (
        <Card>
          <CardHeader className="text-center">
            <CardTitle className="text-xl">Connect to {selectedVendor?.name}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {selectedVendor?.native ? (
              <div className="space-y-4">
                <div className="rounded-lg border border-violet-200 bg-violet-50 p-4 text-center">
                  <Sparkles className="h-6 w-6 text-violet-600 mx-auto mb-2" />
                  <p className="text-sm font-medium text-violet-800">Native integration detected</p>
                  <p className="text-xs text-violet-700 mt-1">Enable Healthcare provides direct API access. Just enter your credentials.</p>
                </div>
                <div className="space-y-2">
                  <Label>Practice ID</Label>
                  <Input placeholder="Enter your Enable Healthcare Practice ID" data-testid="practice-id-input" />
                </div>
                <div className="space-y-2">
                  <Label>API Key</Label>
                  <Input type="password" placeholder="Enter your API key" data-testid="api-key-input" />
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>FHIR Server URL</Label>
                  <Input placeholder="https://fhir.example.com/r4" value={fhirUrl} onChange={(e) => setFhirUrl(e.target.value)} data-testid="fhir-url-input" />
                </div>
                <div className="space-y-2">
                  <Label>Client ID</Label>
                  <Input placeholder="OAuth2 Client ID" value={clientId} onChange={(e) => setClientId(e.target.value)} data-testid="client-id-input" />
                </div>
                <div className="space-y-2">
                  <Label>Client Secret</Label>
                  <Input type="password" placeholder="OAuth2 Client Secret" value={clientSecret} onChange={(e) => setClientSecret(e.target.value)} data-testid="client-secret-input" />
                </div>
              </div>
            )}
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Shield className="h-4 w-4 text-violet-600" />
              <span>All credentials are encrypted and stored securely. HIPAA compliant.</span>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setFhirStep(1)} className="gap-2"><ArrowLeft className="h-4 w-4" /> Back</Button>
              <Button onClick={handleFhirConnect} disabled={connecting} className="flex-1 bg-black hover:bg-gray-800 gap-2" data-testid="connect-btn">
                {connecting ? <><Loader2 className="h-4 w-4 animate-spin" /> Connecting...</> : <>Connect <ArrowRight className="h-4 w-4" /></>}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 3: Authorize Permissions */}
      {fhirStep === 3 && (
        <Card>
          <CardHeader className="text-center">
            <CardTitle className="text-xl">Authorize Access</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground text-center">Lumina needs the following permissions to sync with your EHR:</p>
            <div className="space-y-3">
              {[
                ['Patient Demographics', 'Read patient name, DOB, contact info'],
                ['Appointments', 'Read and sync appointment schedules'],
                ['Clinical Data', 'Read problems, medications, allergies, labs'],
                ['Documents', 'Read and write clinical documents'],
                ['Encounter Notes', 'Write SOAP notes back to the EHR'],
              ].map(([perm, desc]) => (
                <div key={perm} className="flex items-start gap-3 p-3 rounded-lg border">
                  <CheckCircle2 className="h-5 w-5 text-violet-600 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium">{perm}</p>
                    <p className="text-xs text-muted-foreground">{desc}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setFhirStep(2)} className="gap-2"><ArrowLeft className="h-4 w-4" /> Back</Button>
              <Button onClick={handleFhirAuthorize} disabled={connecting} className="flex-1 bg-black hover:bg-gray-800 gap-2" data-testid="authorize-btn">
                {connecting ? <><Loader2 className="h-4 w-4 animate-spin" /> Authorizing...</> : <><CheckCircle2 className="h-4 w-4" /> Authorize & Sync</>}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 4: Success */}
      {fhirStep === 4 && (
        <Card className="text-center" data-testid="fhir-success">
          <CardContent className="p-8 space-y-4">
            <div className="h-16 w-16 rounded-full bg-violet-100 flex items-center justify-center mx-auto">
              <CheckCircle2 className="h-8 w-8 text-violet-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold">Connected to {selectedVendor?.name}</h2>
              <p className="text-sm text-muted-foreground mt-1">Your EHR is now synced with Lumina</p>
            </div>
            <div className="rounded-lg border bg-slate-50 p-4 text-left text-sm space-y-1 max-w-sm mx-auto">
              <div className="flex justify-between"><span className="text-muted-foreground">Appointments</span><span className="font-medium text-violet-600">Syncing</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Patient Charts</span><span className="font-medium text-violet-600">Syncing</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">SOAP Note Push</span><span className="font-medium text-violet-600">Enabled</span></div>
            </div>
            <Button onClick={resetWizard} className="bg-black hover:bg-gray-800 gap-2 mt-2">
              <ArrowRight className="h-4 w-4" /> Go to Dashboard
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default LuminaIntegrations;
